import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { demeterCompanyApi } from '../../../../Apis/Apis';
import applicationSettings from '../../../../Core/Settings/ApplicationSettings';
import applicationConstants from '../../../../Core/Utility/ApplicationConstants';
import {
    AddDemeterCompanyRequest,
    DemeterMarket,
    DemeterMarketLicenseModel,
    DemeterPaymentType,
    DemeterUserType,
    UpdateDemeterCompanyRequest,
} from '../../../../Generated/Raven-Demeter';
import NavigationRoutes from '../../../../Layouts/NavigationRoutes';
import { useApplicationSelector } from '../../../../Redux/ReduxStore';
import { selectUserType } from '../../../../Redux/Slices/UserSlice';
import useApi from '../../../Apis/Hooks/useApiHook';
import useApiWithoutAutoExecute from '../../../Apis/Hooks/useApiWithoutAutoExecute';
import LinkButton, { LinkButtonType } from '../../../Components/Form/Buttons/LinkButton';
import TextInput from '../../../Components/Form/Inputs/TextInput';
import PageHeaderWithBackNavigation from '../../../Components/Headers/PageHeaderWithBackNavigation';
import LoadingSpinner from '../../../Components/LoadingSpinner/LoadingSpinner';
import useSearchParameters from '../../../Components/Navigation/Hooks/useSearchParametersHook';
import InlineTab from '../../../Components/Navigation/InlineTabs/InlineTab';
import InlineTabs from '../../../Components/Navigation/InlineTabs/InlineTabs';
import RegexValidators from '../../../Core/Validation/RegexValidators';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import Permissions from '../Permissions/Permissions';
import styles from './CompaniesPage.module.scss';
import CompanyBillingForm, { UpsertCompanyBillingRequest } from './CompanyBillingForm';
import CompanyDetailsForm, { UpsertCompanyDetailsRequest } from './CompanyDetailsForm';

enum CompanyTabKeys {
    CompanyTab = 'companyTab',
    CompanyDetails = 'companyDetails',
    CompanyBilling = 'companyBilling',
    CompanyLicenses = 'companyLicenses',
    CompanyPermissions = 'companyPermissions',
}

type UpsertDemeterCompanyMarketsRequest = {
    numberOfLicenses?: number | null;
    markets?: DemeterMarket[] | null;
    marketLicenses?: DemeterMarketLicenseModel[] | null;
};

const CompanyPage: React.FC = () => {
    const { demeterCompanyGuid } = useParams();
    const navigate = useNavigate();
    const [translations] = useLanguage();
    const [searchParameters, setSearchParameters] = useSearchParameters();
    const currentUserType = useApplicationSelector(selectUserType);

    // Company hooks.
    const [requestMarkets, setRequestMarkets] = useState<UpsertDemeterCompanyMarketsRequest>();
    const [requestDetail, setRequestDetail] = useState<UpsertCompanyDetailsRequest>();
    const [isRequestDetailValid, setIsRequestDetailValid] = useState<boolean>(false);
    const [requestBilling, setRequestBilling] = useState<UpsertCompanyBillingRequest>();
    const [isRequestBillingValid, setIsRequestBillingValid] = useState<boolean>(false);
    const [companyTabKey, setCompanyTabKey] = useState<CompanyTabKeys>(CompanyTabKeys.CompanyDetails);
    const [request, setRequest] = useState({
        invoiceDate: '' as Date | string | null,
        subscriptionPrice: null as number | null,
        subscriptionFrequency: '',
        termsAndConditionsAccepted: false,
        termsAndConditionsAcceptedAt: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
        phone: '',
        name: '',
        contactPerson: '',
        billingContactName: '',
        billingCompanyName: '',
        billingAddress1: '',
        billingAddress2: '',
        billingCity: '',
        billingState: '',
        billingCountry: '',
        billingPostalCode: '',
        billingPhone: '',
        billingFax: '',
        billingEmail: '',
        billingVatNumber: null as string | null,
        billingSameAsCompanyInformation: false,
        billingCurrency: '',
        billingRegion: '',
        numberOfLicenses: null as number | null,
        markets: null as DemeterMarket[] | null,
        marketLicenses: null as DemeterMarketLicenseModel[] | null,
        paymentType: null as DemeterPaymentType | null,
        futuresAccountTransferConsent: false,
        futuresAccountNumber: '',
        futuresAccountCustomerName: '',
        futuresAccountTitle: '',
        policyNumbers: '',
        notes: '',
    });
    // Actions hooks.
    const [isSaving, setIsSaving] = useState(false);

    const [updateDemeterCompanyLoading, updateDemeterCompany] = useApiWithoutAutoExecute(
        (updateRequest?: UpdateDemeterCompanyRequest) => {
            if (!demeterCompanyGuid || !updateRequest) {
                return null;
            }

            return demeterCompanyApi.updateDemeterCompany(demeterCompanyGuid, updateRequest);
        },
        {
            successMessage: translations.companies.text.companySuccessfullySaved,
            errorMessage: translations.companies.errors.saveCompanyError,
        },
    );

    const [addDemeterCompanyIsLoading, addDemeterCompany, addDemeterCompanyresponse] = useApiWithoutAutoExecute((addRequest?: AddDemeterCompanyRequest) => {
        if (!addRequest) {
            return null;
        }

        return demeterCompanyApi.addDemeterCompany(addRequest);
    });

    useEffect(() => {
        if (!addDemeterCompanyresponse || addDemeterCompanyIsLoading) {
            return;
        }

        navigate(`${NavigationRoutes.Companies}/${addDemeterCompanyresponse.demeterCompanyGuid}/edit`);
    }, [addDemeterCompanyresponse, addDemeterCompanyIsLoading]);

    const [isLoading, , getCompanyResponse] = useApi(() => {
        if (demeterCompanyGuid) {
            return demeterCompanyApi.getDemeterCompany(demeterCompanyGuid);
        }

        return null;
    });

    // Load the Company if there is one.
    useEffect(() => {
        if (isLoading || (!!demeterCompanyGuid && !isLoading && !getCompanyResponse)) {
            return;
        }

        const responseCompany = getCompanyResponse?.demeterCompany!;
        const hasDemeterCompanyGuidAndResponseCompany = !!demeterCompanyGuid && !!responseCompany;
        const detailRequest = {
            name: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.name ?? '' : '',
            address1: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.address1 ?? '' : '',
            address2: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.address2 ?? '' : '',
            city: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.city ?? '' : '',
            state: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.state ?? '' : '',
            country: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.country ?? '' : '',
            postalCode: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.postalCode ?? '' : '',
            phone: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.phone ?? '' : '',
            contactPerson: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.contactPerson ?? '' : '',
            invoiceDate: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.invoiceDate ?? '' : '',
            subscriptionPrice: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.subscriptionPrice ?? null : null,
            subscriptionFrequency: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.subscriptionFrequency ?? '' : '',
            policyNumbers: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.policyNumbers ?? '' : '',
            notes: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.notes ?? '' : '',
        };
        const billingRequest = {
            billingContactName: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.billingContactName ?? '' : '',
            billingCompanyName: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.billingCompanyName ?? '' : '',
            billingAddress1: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.billingAddress1 ?? '' : '',
            billingAddress2: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.billingAddress2 ?? '' : '',
            billingCity: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.billingCity ?? '' : '',
            billingState: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.billingState ?? '' : '',
            billingCountry: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.billingCountry ?? '' : '',
            billingPostalCode: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.billingPostalCode ?? '' : '',
            billingPhone: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.billingPhone ?? '' : '',
            billingFax: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.billingFax ?? '' : '',
            billingEmail: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.billingEmail ?? '' : '',
            billingVatNumber: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.billingVatNumber ?? null : null,
            billingSameAsCompanyInformation: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.billingSameAsCompanyInformation ?? false : false,
            billingCurrency: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.billingCurrency ?? '' : '',
            billingRegion: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.billingRegion ?? '' : '',
            paymentType: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.paymentType ?? null : null,
            futuresAccountTransferConsent: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.futuresAccountTransferConsent ?? false : false,
            futuresAccountNumber: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.futuresAccountNumber ?? '' : '',
            futuresAccountCustomerName: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.futuresAccountCustomerName ?? '' : '',
            futuresAccountTitle: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.futuresAccountTitle ?? '' : '',
        };
        const marketsRequest = {
            numberOfLicenses: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.numberOfLicenses ?? 0 : 0,
            markets: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.markets ?? [] : [],
            marketLicenses: hasDemeterCompanyGuidAndResponseCompany ? responseCompany.marketLicenses ?? [] : [],
        };

        if (
            hasDemeterCompanyGuidAndResponseCompany &&
            (currentUserType === DemeterUserType.Administrator || currentUserType === DemeterUserType.BusinessOwner)
        ) {
            setIsRequestBillingValid(true);
        }

        setRequestDetail(detailRequest);
        setRequestBilling(billingRequest);
        setRequestMarkets(marketsRequest);
        setRequest({ ...request, ...detailRequest, ...billingRequest, ...marketsRequest });
    }, [isLoading, demeterCompanyGuid, getCompanyResponse]);

    useEffect(() => {
        if (!isRequestDetailValid || !requestDetail) {
            return;
        }

        setRequest({
            ...request,
            name: requestDetail.name ?? '',
            address1: requestDetail.address1 ?? '',
            address2: requestDetail.address2 ?? '',
            city: requestDetail.city ?? '',
            state: requestDetail.state ?? '',
            country: requestDetail.country ?? '',
            postalCode: requestDetail.postalCode ?? '',
            phone: requestDetail.phone ?? '',
            contactPerson: requestDetail.contactPerson ?? '',
            invoiceDate: requestDetail.invoiceDate ?? '',
            subscriptionPrice: requestDetail.subscriptionPrice ?? null,
            subscriptionFrequency: requestDetail.subscriptionFrequency ?? '',
            policyNumbers: requestDetail.policyNumbers ?? '',
            notes: requestDetail.notes ?? '',
        });
    }, [requestDetail, isRequestDetailValid]);

    useEffect(() => {
        if (!requestBilling) {
            return;
        }

        setRequest({
            ...request,
            billingSameAsCompanyInformation: requestBilling.billingSameAsCompanyInformation ?? false,
            billingFax: requestBilling.billingFax ?? '',
            billingEmail: requestBilling.billingEmail ?? '',
            billingVatNumber: requestBilling.billingVatNumber ?? null,
            paymentType: requestBilling.paymentType ?? null,
        });
    }, [requestBilling]);

    useEffect(() => {
        if (!requestMarkets) {
            return;
        }

        setRequest({
            ...request,
            numberOfLicenses: requestMarkets.numberOfLicenses ?? 0,
            markets: requestMarkets.markets ?? [],
            marketLicenses: requestMarkets.marketLicenses ?? [],
        });
    }, [requestMarkets]);

    const searchParametersAreCompanyDetails = searchParameters.companyTab === CompanyTabKeys.CompanyDetails;
    const searchParametersAreCompanyBilling = searchParameters.companyTab === CompanyTabKeys.CompanyBilling;
    const searchParametersAreCompanyLicenses = searchParameters.companyTab === CompanyTabKeys.CompanyLicenses;

    const navigateBackToCompanies = () => navigate(-1);

    const handleClickPaginationAndAdd = () => {
        if ((searchParametersAreCompanyDetails || companyTabKey === CompanyTabKeys.CompanyDetails) && isRequestDetailValid) {
            setCompanyTabKey(CompanyTabKeys.CompanyBilling);
            setSearchParameters({ [CompanyTabKeys.CompanyTab]: CompanyTabKeys.CompanyBilling });
        }

        if (searchParametersAreCompanyBilling && isRequestDetailValid && isRequestBillingValid) {
            setCompanyTabKey(CompanyTabKeys.CompanyLicenses);
            setSearchParameters({ [CompanyTabKeys.CompanyTab]: CompanyTabKeys.CompanyLicenses });
        }

        if (searchParametersAreCompanyLicenses) {
            upsertCompany();
        }
    };

    const upsertCompany = () => {
        if (!isRequestDetailValid || !isRequestBillingValid || !requestDetail || !requestBilling || !requestMarkets || !request) {
            setIsSaving(false);
            return;
        }

        const upsertCompanyRequest = { ...request };
        upsertCompanyRequest.billingCompanyName =
            !requestBilling.billingSameAsCompanyInformation || !isRequestDetailValid ? requestBilling.billingCompanyName ?? '' : requestDetail.name ?? '';
        upsertCompanyRequest.billingContactName =
            !requestBilling.billingSameAsCompanyInformation || !isRequestDetailValid ? requestBilling.billingContactName ?? '' : requestDetail.contactPerson;
        upsertCompanyRequest.billingAddress1 =
            !requestBilling.billingSameAsCompanyInformation || !isRequestDetailValid ? requestBilling.billingAddress1 ?? '' : requestDetail.address1;
        upsertCompanyRequest.billingAddress2 =
            !requestBilling.billingSameAsCompanyInformation || !isRequestDetailValid ? requestBilling.billingAddress2 ?? '' : requestDetail.address2;
        upsertCompanyRequest.billingCity =
            !requestBilling.billingSameAsCompanyInformation || !isRequestDetailValid ? requestBilling.billingCity ?? '' : requestDetail.city;
        upsertCompanyRequest.billingState =
            !requestBilling.billingSameAsCompanyInformation || !isRequestDetailValid ? requestBilling.billingState ?? '' : requestDetail.state;
        upsertCompanyRequest.billingCountry =
            !requestBilling.billingSameAsCompanyInformation || !isRequestDetailValid ? requestBilling.billingCountry ?? '' : requestDetail.country;
        upsertCompanyRequest.billingPostalCode =
            !requestBilling.billingSameAsCompanyInformation || !isRequestDetailValid ? requestBilling.billingPostalCode ?? '' : requestDetail.postalCode;
        upsertCompanyRequest.billingPhone =
            !requestBilling.billingSameAsCompanyInformation || !isRequestDetailValid ? requestBilling.billingPhone ?? '' : requestDetail.phone;

        if (upsertCompanyRequest.invoiceDate) {
            upsertCompanyRequest.invoiceDate = formattingService.toApiDate(upsertCompanyRequest.invoiceDate);
        } else {
            upsertCompanyRequest.invoiceDate = null;
        }

        if (
            currentUserType !== DemeterUserType.BusinessOwner &&
            (!upsertCompanyRequest.phone || (upsertCompanyRequest.phone && upsertCompanyRequest.phone.length === 0))
        ) {
            upsertCompanyRequest.phone = '';
        }

        if (demeterCompanyGuid) {
            const updateCompanyRequest = { ...upsertCompanyRequest, demeterCompanyGuid } as UpdateDemeterCompanyRequest;
            updateDemeterCompany(updateCompanyRequest);
        }

        if (!demeterCompanyGuid) {
            const addCompanyRequest = upsertCompanyRequest as AddDemeterCompanyRequest;
            addDemeterCompany(addCompanyRequest);
        }
    };

    const updateMarketLicense = (market: DemeterMarket, value: string) => {
        const availableMarkets = applicationSettings.markets || [];
        const updatedMarketLicenses = availableMarkets.map((availableMarket) => {
            const existingLicense = requestMarkets?.marketLicenses?.find((license) => license.market === availableMarket);

            if (availableMarket === market) {
                return {
                    market: availableMarket,
                    numberOfLicenses: +value,
                    numberOfLicensesUsed: existingLicense?.numberOfLicensesUsed || 0,
                };
            }

            return (
                existingLicense || {
                    market: availableMarket,
                    numberOfLicenses: 0,
                    numberOfLicensesUsed: 0,
                }
            );
        });

        const totalLicenses = updatedMarketLicenses.reduce((sum, license) => sum + (license.numberOfLicenses || 0), 0);

        setRequestMarkets({
            ...requestMarkets,
            marketLicenses: updatedMarketLicenses,
            numberOfLicenses: totalLicenses,
        });
    };

    const isAddCompanyOrNextButtonIsDisabled = useMemo(() => {
        if (isSaving || !isRequestDetailValid) {
            return true;
        }

        if (searchParametersAreCompanyDetails && !isRequestDetailValid) {
            return true;
        }

        if (searchParametersAreCompanyBilling && (!isRequestDetailValid || !isRequestBillingValid)) {
            return true;
        }

        if (searchParametersAreCompanyLicenses && (!isRequestDetailValid || !isRequestBillingValid)) {
            return true;
        }

        return false;
    }, [
        isSaving,
        isRequestDetailValid,
        isRequestBillingValid,
        searchParameters,
        searchParametersAreCompanyDetails,
        searchParametersAreCompanyBilling,
        searchParametersAreCompanyLicenses,
    ]);

    return addDemeterCompanyIsLoading ? (
        <LoadingSpinner />
    ) : (
        <div className={styles.add_edit_company_page_container}>
            <PageHeaderWithBackNavigation
                handleBackNavigation={navigateBackToCompanies}
                title={demeterCompanyGuid ? requestDetail?.name ?? '' : translations.companies.title.add}
                track={{ eventCategory: 'Company', eventAction: 'BackClicked' }}
            />
            <InlineTabs inlineTabsKey={CompanyTabKeys.CompanyTab} track={{ eventCategory: 'Company', eventAction: 'Tab' }}>
                <InlineTab show title={translations.companies.tabs.details} inlineTabKey={CompanyTabKeys.CompanyDetails} testId="CompanyDetailsTab">
                    {requestDetail && (
                        <CompanyDetailsForm
                            companyDetails={requestDetail}
                            handleCompanyDetailsChange={setRequestDetail}
                            handleCompanyDetailsIsValid={setIsRequestDetailValid}
                        />
                    )}
                </InlineTab>
                <InlineTab show title={translations.companies.tabs.billing} inlineTabKey={CompanyTabKeys.CompanyBilling} testId="CompanyBillingTab">
                    {requestBilling && (
                        <CompanyBillingForm
                            userCompanyName={demeterCompanyGuid && requestDetail?.name ? requestDetail.name : null}
                            companyBilling={requestBilling}
                            handleCompanyBillingChange={setRequestBilling}
                            handleCompanyBillingIsValid={setIsRequestBillingValid}
                        />
                    )}
                </InlineTab>
                <InlineTab show title={translations.companies.tabs.licenses} inlineTabKey={CompanyTabKeys.CompanyLicenses} testId="CompanyLicensesTab">
                    <>
                        <div className={styles.add_edit_company_license_container}>
                            {applicationSettings.markets?.map((market) => {
                                const marketLicense = requestMarkets?.marketLicenses?.find((license) => license.market === market);
                                return (
                                    <div key={market} className={styles.add_edit_company_input_sm}>
                                        <TextInput
                                            title={translations.market[market]}
                                            type="number"
                                            value={`${marketLicense?.numberOfLicenses ?? 0}`}
                                            handleTextChange={(value: string) => updateMarketLicense(market, value)}
                                            validation={RegexValidators.Numeric}
                                            track={{
                                                eventCategory: 'Company',
                                                eventAction: 'MarketLicenseChanged',
                                                eventValue: marketLicense?.numberOfLicenses,
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        <div className={styles.add_edit_company_input}>
                            <p>
                                <strong>{translations.companies.fields.totalLicenses}</strong>
                            </p>
                            <p>{requestMarkets?.numberOfLicenses ?? applicationConstants.TablePlaceholderZeroOrEmpty}</p>
                        </div>
                    </>
                </InlineTab>
                <InlineTab
                    show={!!demeterCompanyGuid}
                    title={translations.companies.tabs.permissions}
                    inlineTabKey={CompanyTabKeys.CompanyPermissions}
                    testId="CompanyPermissionsTab"
                >
                    <div>{demeterCompanyGuid && <Permissions demeterCompanyGuid={demeterCompanyGuid} />}</div>
                </InlineTab>
            </InlineTabs>
            {searchParameters.companyTab !== CompanyTabKeys.CompanyPermissions && (
                <div className={styles.add_edit_company_submit_action_row}>
                    <LinkButton
                        title={translations.actions.cancel}
                        onClick={navigateBackToCompanies}
                        type={LinkButtonType.White}
                        track={{ eventCategory: 'Company', eventAction: 'CancelClicked' }}
                    />
                    {demeterCompanyGuid && (
                        <LinkButton
                            title={translations.actions.save}
                            type={LinkButtonType.Blue}
                            onClick={() => {
                                // Set this here and let the async upsertCompany do its job.
                                setIsSaving(true);
                                upsertCompany();
                            }}
                            disabled={
                                updateDemeterCompanyLoading ||
                                (currentUserType !== DemeterUserType.BusinessOwner &&
                                    currentUserType !== DemeterUserType.Administrator &&
                                    (!isRequestDetailValid || !isRequestBillingValid || isSaving))
                            }
                            track={{ eventCategory: 'Company', eventAction: 'SaveClicked' }}
                        />
                    )}
                    {!demeterCompanyGuid && (
                        <LinkButton
                            title={searchParameters.companyTab !== CompanyTabKeys.CompanyLicenses ? translations.actions.next : translations.actions.add}
                            type={LinkButtonType.Blue}
                            onClick={handleClickPaginationAndAdd}
                            disabled={isAddCompanyOrNextButtonIsDisabled}
                            track={{ eventCategory: 'Company', eventAction: 'NextOrAddClicked' }}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default CompanyPage;
